<template>
  <div class="facilities-page">
    <!-- Navigation Bar -->
    <nav class="navbar navbar-expand-lg navbar-light bg-white shadow-sm fixed-top">
      <div class="container">
        <a class="navbar-brand d-flex align-items-center" href="#home">
          <img src="https://pona.paysol.africa/logos/pona.png" alt="PONA HEALTH-X Logo" height="50" class="me-3">
        </a>
        <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
          <span class="navbar-toggler-icon"></span>
        </button>
        <div class="collapse navbar-collapse" id="navbarNav">
          <ul class="navbar-nav ms-auto align-items-center">
            <li class="nav-item">
              <a class="nav-link" href="javascript: void(0);" @click="goToPage('/')">Home</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="javascript: void(0);" @click="goToPage('about')">About</a>
            </li>
            <li class="nav-item d-none">
              <a class="nav-link" href="#how-it-works">How It Works</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#benefits">Benefits</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#services">Health Services</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#enrollment">Enrollment</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#facilities">Health Facilities</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#faqs">FAQs</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#contact">Contact</a>
            </li>
            <li class="nav-item dropdown ms-3">
              <button class="btn btn-primary dropdown-toggle" type="button"
                      id="loginDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                <i class="bx bx-user me-2"></i>Login
              </button>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="loginDropdown">
                <li>
                  <a class="dropdown-item" @click="goToMemberLogin">
                    <i class="bx bx-user-circle me-2"></i>Member Portal
                  </a>
                </li>
                <li><hr class="dropdown-divider"></li>
                <li>
                  <a class="dropdown-item" @click="goToStaffLogin">
                    <i class="bx bx-briefcase me-2"></i>Staff Login
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <!-- Page Header -->
    <section class="page-header">
      <div class="header-map-bg"></div>
      <div class="header-overlay"></div>
      <div class="container">
        <div class="row align-items-center min-vh-50 py-5">
          <div class="col-lg-7 text-white position-relative z-2">
            <nav aria-label="breadcrumb" class="mb-4">
              <ol class="breadcrumb breadcrumb-light">
                <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
                <li class="breadcrumb-item active" aria-current="page">Facilities</li>
              </ol>
            </nav>
            <h1 class="display-3 fw-bold mb-4 animate-on-scroll text-white">
              Health Facilities Network
            </h1>
            <p class="lead mb-4 animate-on-scroll">
              Access quality healthcare at over 2,500 contracted facilities across the country.
              From community health posts to specialized hospitals, PONA HEALTH-X coverage extends throughout the nation.
            </p>
            <div class="header-stats-row d-flex flex-wrap gap-3 animate-on-scroll">
              <div class="header-stat-item">
                <span class="stat-number">2,500+</span>
                <span class="stat-label">Total Facilities</span>
              </div>
              <div class="header-stat-item">
                <span class="stat-number">47</span>
                <span class="stat-label">Counties Covered</span>
              </div>
              <div class="header-stat-item">
                <span class="stat-number">95%</span>
                <span class="stat-label">District Coverage</span>
              </div>
            </div>
          </div>
          <div class="col-lg-5 d-none d-lg-block animate-on-scroll">
            <div class="header-facility-cards">
              <div class="facility-float-card card-1">
                <div class="ffc-icon bg-info"><i class="bx bx-home-heart"></i></div>
                <div class="ffc-content">
                  <span class="ffc-number">1,800+</span>
                  <span class="ffc-label">Health Posts</span>
                </div>
              </div>
              <div class="facility-float-card card-2">
                <div class="ffc-icon bg-success"><i class="bx bx-plus-medical"></i></div>
                <div class="ffc-content">
                  <span class="ffc-number">550+</span>
                  <span class="ffc-label">Health Centers</span>
                </div>
              </div>
              <div class="facility-float-card card-3">
                <div class="ffc-icon bg-primary"><i class="bx bx-hospital"></i></div>
                <div class="ffc-content">
                  <span class="ffc-number">150+</span>
                  <span class="ffc-label">Hospitals</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Facility Overview Stats -->
    <section class="stats-section">
      <div class="container">
        <div class="stats-card animate-on-scroll">
          <div class="row g-4">
            <div class="col-lg-3 col-md-6" v-for="stat in overviewStats" :key="stat.label">
              <div class="stat-item">
                <div class="stat-icon" :class="'bg-' + stat.color">
                  <i :class="stat.icon"></i>
                </div>
                <div class="stat-content">
                  <span class="stat-value">{{ stat.value }}</span>
                  <span class="stat-name">{{ stat.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Facility Types Section -->
    <section class="py-5 bg-white">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-category"></i></span>
            <span class="label-text">Facility Types</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Types of Health Facilities</h2>
          <p class="lead text-muted">Understanding the different levels of care in the health system</p>
        </div>

        <div class="facility-types-grid">
          <div class="facility-type-card animate-on-scroll"
               v-for="(facility, index) in facilityTypes"
               :key="facility.title"
               :class="'type-' + facility.colorClass">
            <div class="type-level">Level {{ index + 1 }}</div>
            <div class="type-icon">
              <i :class="facility.icon"></i>
            </div>
            <h3 class="fw-bold mb-3">{{ facility.title }}</h3>
            <p class="type-description">{{ facility.description }}</p>

            <div class="type-stats">
              <div class="ts-item">
                <span class="ts-value">{{ facility.count }}</span>
                <span class="ts-label">Facilities</span>
              </div>
              <div class="ts-item">
                <span class="ts-value">{{ facility.population }}</span>
                <span class="ts-label">Population Served</span>
              </div>
            </div>

            <div class="type-services">
              <h6 class="fw-bold mb-2">Services Provided:</h6>
              <ul>
                <li v-for="service in facility.services" :key="service">
                  <i class="bx bx-check"></i>{{ service }}
                </li>
              </ul>
            </div>

            <div class="type-staff" v-if="facility.staff">
              <h6 class="fw-bold mb-2">Staff:</h6>
              <div class="staff-tags">
                <span v-for="staff in facility.staff" :key="staff">{{ staff }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Health System Hierarchy -->
    <section class="py-5 bg-light">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-git-branch"></i></span>
            <span class="label-text">Structure</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Health System Hierarchy</h2>
          <p class="lead text-muted">How facilities are organized in the healthcare system</p>
        </div>

        <div class="hierarchy-container animate-on-scroll">
          <div class="hierarchy-level" v-for="(level, index) in hierarchyLevels" :key="level.title">
            <div class="level-connector" v-if="index > 0"></div>
            <div class="level-card" :class="'level-' + level.color">
              <div class="level-icon">
                <i :class="level.icon"></i>
              </div>
              <div class="level-content">
                <h5 class="fw-bold mb-1">{{ level.title }}</h5>
                <p class="mb-2">{{ level.description }}</p>
                <div class="level-facilities">
                  <span v-for="facility in level.facilities" :key="facility" class="facility-tag">
                    {{ facility }}
                  </span>
                </div>
              </div>
              <div class="level-coverage">
                <span class="coverage-label">Coverage:</span>
                <span class="coverage-value">{{ level.coverage }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Regional Coverage -->
    <section class="py-5">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-map"></i></span>
            <span class="label-text">Coverage</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Regional Facility Distribution</h2>
          <p class="lead text-muted">PONA HEALTH-X-contracted facilities across all regions</p>
        </div>

        <div class="row g-4">
          <div class="col-lg-4 col-md-6 animate-on-scroll" v-for="region in regions" :key="region.name">
            <div class="region-card h-100">
              <div class="region-header" :style="{ background: region.gradient }">
                <h5 class="fw-bold text-white mb-0">{{ region.name }}</h5>
                <span class="region-badge">{{ region.districts }} Districts</span>
              </div>
              <div class="region-body">
                <div class="region-stats">
                  <div class="rs-item">
                    <i class="bx bx-home-heart text-info"></i>
                    <div class="rs-content">
                      <span class="rs-value">{{ region.healthPosts }}</span>
                      <span class="rs-label">Health Posts</span>
                    </div>
                  </div>
                  <div class="rs-item">
                    <i class="bx bx-plus-medical text-success"></i>
                    <div class="rs-content">
                      <span class="rs-value">{{ region.healthCenters }}</span>
                      <span class="rs-label">Health Centers</span>
                    </div>
                  </div>
                  <div class="rs-item">
                    <i class="bx bx-hospital text-primary"></i>
                    <div class="rs-content">
                      <span class="rs-value">{{ region.hospitals }}</span>
                      <span class="rs-label">Hospitals</span>
                    </div>
                  </div>
                </div>
                <div class="region-coverage">
                  <div class="coverage-bar">
                    <div class="coverage-fill" :style="{ width: region.coverage + '%' }"></div>
                  </div>
                  <span class="coverage-text">{{ region.coverage }}% PONA HEALTH-X Coverage</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Referral System -->
    <section class="py-5 bg-gradient-primary text-white">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label-light justify-content-center">
            <span class="label-icon"><i class="bx bx-transfer"></i></span>
            <span class="label-text">Referral System</span>
          </div>
          <h2 class="display-5 fw-bold mb-3 text-white">How Referrals Work</h2>
          <p class="lead opacity-75">Understanding the patient referral pathway between facilities</p>
        </div>

        <div class="referral-pathway animate-on-scroll">
          <div class="pathway-step" v-for="(step, index) in referralSteps" :key="step.title">
            <div class="pathway-icon">
              <i :class="step.icon"></i>
            </div>
            <div class="pathway-content">
              <h5 class="fw-bold mb-2">{{ step.title }}</h5>
              <p class="opacity-75 mb-0">{{ step.description }}</p>
            </div>
            <div class="pathway-arrow" v-if="index < referralSteps.length - 1">
              <i class="bx bx-right-arrow-alt"></i>
            </div>
          </div>
        </div>

        <div class="referral-notes animate-on-scroll">
          <div class="note-card">
            <i class="bx bx-info-circle"></i>
            <div class="note-content">
              <h6 class="fw-bold mb-1">Referral Requirements</h6>
              <p class="mb-0">A referral letter from your primary facility is required for higher-level care. Emergency cases are exempt from this requirement and can access any facility directly.</p>
            </div>
          </div>
          <div class="note-card">
            <i class="bx bx-check-shield"></i>
            <div class="note-content">
              <h6 class="fw-bold mb-1">Coverage Continuity</h6>
              <p class="mb-0">Your PONA HEALTH-X coverage remains valid throughout the referral chain. All contracted facilities at any level will honor your membership card.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Services by Facility Type -->
    <section class="py-5">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-list-check"></i></span>
            <span class="label-text">Services</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Services by Facility Level</h2>
          <p class="lead text-muted">What services are available at each type of facility</p>
        </div>

        <div class="services-comparison animate-on-scroll">
          <div class="comparison-header">
            <div class="ch-service">Service</div>
            <div class="ch-facility" v-for="facility in comparisonFacilities" :key="facility">
              {{ facility }}
            </div>
          </div>
          <div class="comparison-row" v-for="service in serviceComparison" :key="service.name">
            <div class="cr-service">
              <i :class="service.icon"></i>
              <span>{{ service.name }}</span>
            </div>
            <div class="cr-facility" v-for="(available, index) in service.availability" :key="index">
              <i :class="available ? 'bx bx-check text-success' : 'bx bx-x text-muted'"></i>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Quality Standards -->
    <section class="py-5 bg-light">
      <div class="container py-5">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-5 mb-lg-0 animate-on-scroll">
            <div class="section-label">
              <span class="label-icon"><i class="bx bx-shield-check"></i></span>
              <span class="label-text">Quality</span>
            </div>
            <h2 class="display-5 fw-bold mb-4">
              Facility
              <span class="text-gradient">Quality Standards</span>
            </h2>
            <p class="lead text-muted mb-4">
              All PONA HEALTH-X-contracted facilities must meet strict quality standards set by the
              Ministry of Health to ensure you receive safe and effective healthcare.
            </p>

            <div class="quality-checklist">
              <div class="checklist-item" v-for="item in qualityStandards" :key="item.title">
                <div class="checklist-icon">
                  <i :class="item.icon"></i>
                </div>
                <div class="checklist-content">
                  <h6 class="fw-bold mb-1">{{ item.title }}</h6>
                  <p class="text-muted small mb-0">{{ item.description }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-6 animate-on-scroll">
            <div class="quality-image-container">
              <img src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=600"
                   alt="Quality Healthcare Facility"
                   class="img-fluid rounded-4">
              <div class="quality-badge">
                <i class="bx bx-certification"></i>
                <span>MOH Accredited</span>
              </div>
              <div class="quality-stats-card">
                <div class="qs-item">
                  <span class="qs-value">100%</span>
                  <span class="qs-label">Accredited</span>
                </div>
                <div class="qs-divider"></div>
                <div class="qs-item">
                  <span class="qs-value">Annual</span>
                  <span class="qs-label">Inspections</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Finding a Facility -->
    <section class="py-5">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-search-alt"></i></span>
            <span class="label-text">Find Facility</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">How to Find a Facility</h2>
          <p class="lead text-muted">Ways to locate PONA HEALTH-X-contracted facilities near you</p>
        </div>

        <div class="row g-4">
          <div class="col-lg-4 animate-on-scroll" v-for="method in findMethods" :key="method.title">
            <div class="find-card h-100">
              <div class="find-icon" :class="'bg-' + method.color">
                <i :class="method.icon"></i>
              </div>
              <h4 class="fw-bold mb-3">{{ method.title }}</h4>
              <p class="text-muted mb-4">{{ method.description }}</p>
              <div class="find-details">
                <div class="detail-row" v-for="detail in method.details" :key="detail.label">
                  <i :class="detail.icon"></i>
                  <div>
                    <span class="detail-label">{{ detail.label }}</span>
                    <span class="detail-value">{{ detail.value }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact Card -->
        <div class="facility-contact-card animate-on-scroll">
          <div class="row align-items-center">
            <div class="col-lg-8">
              <h4 class="fw-bold mb-2">Need Help Finding a Facility?</h4>
              <p class="text-muted mb-lg-0">
                Contact our hotline for assistance in locating the nearest
                PONA HEALTH-X-contracted facility in your area.
              </p>
            </div>
            <div class="col-lg-4 text-lg-end">
              <a href="tel:+254700000000" class="btn btn-primary btn-lg">
                <i class="bx bx-phone me-2"></i>Call Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Facility Amenities -->
    <section class="py-5 bg-light">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-check-square"></i></span>
            <span class="label-text">Amenities</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">What to Expect at Facilities</h2>
          <p class="lead text-muted">Standard amenities and features at PONA HEALTH-X-contracted facilities</p>
        </div>

        <div class="row g-4">
          <div class="col-lg-3 col-md-6 animate-on-scroll" v-for="amenity in amenities" :key="amenity.title">
            <div class="amenity-card h-100">
              <div class="amenity-icon">
                <i :class="amenity.icon"></i>
              </div>
              <h5 class="fw-bold mb-2">{{ amenity.title }}</h5>
              <p class="text-muted small mb-0">{{ amenity.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-5">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-help-circle"></i></span>
            <span class="label-text">FAQs</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Facility Questions</h2>
          <p class="lead text-muted">Common questions about PONA HEALTH-X facilities</p>
        </div>

        <div class="row">
          <div class="col-lg-10 mx-auto">
            <div class="accordion" id="facilitiesFAQ">
              <div class="accordion-item animate-on-scroll" v-for="(faq, index) in faqs" :key="index">
                <h2 class="accordion-header">
                  <button class="accordion-button" :class="{ collapsed: index !== 0 }"
                          type="button" data-bs-toggle="collapse"
                          :data-bs-target="'#faq' + index">
                    <i class="bx bx-help-circle me-3 text-primary"></i>
                    {{ faq.question }}
                  </button>
                </h2>
                <div :id="'faq' + index"
                     class="accordion-collapse collapse"
                     :class="{ show: index === 0 }"
                     data-bs-parent="#facilitiesFAQ">
                  <div class="accordion-body">{{ faq.answer }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-5 bg-gradient-primary text-white">
      <div class="container py-5">
        <div class="row align-items-center">
          <div class="col-lg-8 animate-on-scroll">
            <h2 class="display-5 fw-bold mb-3 text-white">Ready to Access Our Facility Network?</h2>
            <p class="lead mb-4 mb-lg-0 opacity-75">
              Join PONA HEALTH-X today and gain access to over 2,500 health facilities across the country.
              Quality healthcare is closer than you think.
            </p>
          </div>
          <div class="col-lg-4 text-lg-end animate-on-scroll">
            <router-link to="/enrollment" class="btn btn-light btn-lg mb-3 me-2">
              <i class="bx bx-user-plus me-2"></i>Enroll Now
            </router-link>
            <router-link to="/contact" class="btn btn-outline-light btn-lg mb-3">
              <i class="bx bx-phone me-2"></i>Contact Us
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-dark text-white py-5">
      <div class="container">
        <div class="row g-4">
          <div class="col-lg-4">
            <div class="d-flex align-items-center mb-4">
              <img src="https://pona.paysol.africa/logos/pona.png" alt="PONA HEALTH-X Logo" height="40" class="me-3" style="background: white; padding: 5px; border-radius: 8px;">
              <div>
                <div class="fw-bold">PONA HEALTH-X</div>
                <div class="small text-white-50">Community-Based Health Insurance</div>
              </div>
            </div>
            <p class="text-white-50 mb-4">
              Providing access to quality healthcare through an extensive network of facilities.
            </p>
            <div class="social-links">
              <a href="#" class="social-link"><i class="bx bxl-facebook-circle"></i></a>
              <a href="#" class="social-link"><i class="bx bxl-twitter"></i></a>
              <a href="#" class="social-link"><i class="bx bxl-linkedin-square"></i></a>
              <a href="#" class="social-link"><i class="bx bxl-youtube"></i></a>
            </div>
          </div>
          <div class="col-lg-2 col-md-6">
            <h6 class="fw-bold mb-3 text-white">Quick Links</h6>
            <ul class="footer-links">
              <li><router-link to="/">Home</router-link></li>
              <li><router-link to="/about">About Us</router-link></li>
              <li><router-link to="/benefits">Benefits</router-link></li>
              <li><router-link to="/services">Services</router-link></li>
            </ul>
          </div>
          <div class="col-lg-2 col-md-6 text-white">
            <h6 class="fw-bold mb-3 text-white">Resources</h6>
            <ul class="footer-links">
              <li><router-link to="/facilities">Facilities</router-link></li>
              <li><router-link to="/enrollment">Enrollment</router-link></li>
              <li><a href="#">FAQs</a></li>
              <li><router-link to="/contact">Contact</router-link></li>
            </ul>
          </div>
          <div class="col-lg-4">
            <h6 class="fw-bold mb-3 text-white">Contact Us</h6>
            <ul class="footer-contact">
              <li><i class="bx bx-map me-2"></i>Headquarters</li>
              <li><i class="bx bx-phone me-2"></i>Call: +254 700 000 000</li>
              <li><i class="bx bx-envelope me-2"></i>info@ponahealth.com</li>
            </ul>
          </div>
        </div>
        <hr class="my-4 opacity-25">
        <div class="row align-items-center">
          <div class="col-md-6 text-center text-md-start">
            <p class="mb-0 text-white-50 small">
              © 2025 PONA HEALTH-X. All rights reserved.
            </p>
          </div>
          <div class="col-md-6 text-center text-md-end">
            <a href="#" class="text-white-50 small text-decoration-none me-3">Privacy Policy</a>
            <a href="#" class="text-white-50 small text-decoration-none">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>

    <!-- Scroll to Top -->
    <button class="scroll-top-btn" @click="scrollToTop" v-show="showScrollTop">
      <i class="bx bx-chevron-up"></i>
    </button>
  </div>
</template>

<script>
export default {
  name: 'PonaHealthFacilitiesPage',

  data() {
    return {
      showScrollTop: false,

      overviewStats: [
        { icon: 'bx bx-home-heart', value: '1,800+', label: 'Health Posts', color: 'info' },
        { icon: 'bx bx-plus-medical', value: '550+', label: 'Health Centers', color: 'success' },
        { icon: 'bx bx-hospital', value: '150+', label: 'Hospitals', color: 'primary' },
        { icon: 'bx bx-user-plus', value: '8,500+', label: 'Health Workers', color: 'warning' }
      ],

      facilityTypes: [
        {
          icon: 'bx bx-home-heart',
          title: 'Health Posts',
          description: 'Community-level primary care facilities located within wards, providing essential preventive and basic curative services.',
          count: '1,800+',
          population: '3,000-5,000',
          colorClass: 'info',
          services: [
            'Health education',
            'Immunization',
            'Family planning',
            'Basic first aid',
            'Malaria testing & treatment',
            'Referral services'
          ],
          staff: ['Community Health Workers (2)']
        },
        {
          icon: 'bx bx-plus-medical',
          title: 'Health Centers',
          description: 'Primary healthcare facilities serving larger populations with outpatient and basic inpatient services.',
          count: '550+',
          population: '15,000-25,000',
          colorClass: 'success',
          services: [
            'Outpatient care',
            'Maternal & child health',
            'Laboratory services',
            'Pharmacy',
            'Minor surgery',
            'Emergency care'
          ],
          staff: ['Clinical Officers', 'Nurses', 'Midwives', 'Lab Technicians']
        },
        {
          icon: 'bx bx-building',
          title: 'Sub-County Hospitals',
          description: 'District-level hospitals providing comprehensive inpatient and outpatient services with surgical capacity.',
          count: '100+',
          population: '60,000-100,000',
          colorClass: 'warning',
          services: [
            'Inpatient care',
            'Surgery',
            'Emergency services',
            'Imaging (X-ray)',
            'Blood bank',
            'Specialist consultations'
          ],
          staff: ['Medical Officers', 'Surgeons', 'Specialists', 'Nurses']
        },
        {
          icon: 'bx bx-hospital',
          title: 'County & Referral Hospitals',
          description: 'Higher-level facilities providing specialized services and serving as referral centers for complex cases.',
          count: '50+',
          population: '500K-2M',
          colorClass: 'primary',
          services: [
            'Specialized care',
            'Advanced surgery',
            'ICU services',
            'CT/MRI imaging',
            'Dialysis',
            'Cancer treatment'
          ],
          staff: ['Specialists', 'Sub-specialists', 'Consultants', 'Residents']
        }
      ],

      hierarchyLevels: [
        {
          icon: 'bx bx-home-heart',
          title: 'Community Level',
          description: 'Community-based primary healthcare',
          facilities: ['Health Posts', 'Community Health Program'],
          coverage: '3,000-5,000 people',
          color: 'info'
        },
        {
          icon: 'bx bx-plus-medical',
          title: 'Ward Level',
          description: 'Primary healthcare unit',
          facilities: ['Health Centers', 'Dispensaries'],
          coverage: '15,000-100,000 people',
          color: 'success'
        },
        {
          icon: 'bx bx-building',
          title: 'Sub-County Level',
          description: 'Secondary healthcare services',
          facilities: ['Sub-County Hospitals', 'District Hospitals'],
          coverage: '100K-500K people',
          color: 'warning'
        },
        {
          icon: 'bx bx-hospital',
          title: 'County/National Level',
          description: 'Tertiary and specialized care',
          facilities: ['County Hospitals', 'National Referral Hospitals'],
          coverage: '500K-5M people',
          color: 'primary'
        }
      ],

      regions: [
        { name: 'Central Region', districts: 8, healthPosts: 280, healthCenters: 85, hospitals: 28, coverage: 95, gradient: 'linear-gradient(135deg, #2c5aa0, #4a90e2)' },
        { name: 'Western Region', districts: 12, healthPosts: 350, healthCenters: 95, hospitals: 22, coverage: 92, gradient: 'linear-gradient(135deg, #28a745, #5cb85c)' },
        { name: 'Eastern Region', districts: 10, healthPosts: 320, healthCenters: 78, hospitals: 18, coverage: 88, gradient: 'linear-gradient(135deg, #dc3545, #f1707a)' },
        { name: 'Rift Valley', districts: 14, healthPosts: 420, healthCenters: 110, hospitals: 32, coverage: 90, gradient: 'linear-gradient(135deg, #ffc107, #ffda6a)' },
        { name: 'Coastal Region', districts: 6, healthPosts: 180, healthCenters: 52, hospitals: 15, coverage: 85, gradient: 'linear-gradient(135deg, #17a2b8, #5bc0de)' },
        { name: 'Northern Region', districts: 8, healthPosts: 150, healthCenters: 45, hospitals: 12, coverage: 78, gradient: 'linear-gradient(135deg, #6f42c1, #9d7bd1)' },
        { name: 'North Eastern', districts: 5, healthPosts: 80, healthCenters: 28, hospitals: 8, coverage: 65, gradient: 'linear-gradient(135deg, #fd7e14, #ffb347)' },
        { name: 'Nyanza Region', districts: 9, healthPosts: 260, healthCenters: 72, hospitals: 20, coverage: 91, gradient: 'linear-gradient(135deg, #e83e8c, #f8a5c2)' },
        { name: 'Southern Region', districts: 7, healthPosts: 210, healthCenters: 58, hospitals: 16, coverage: 87, gradient: 'linear-gradient(135deg, #20c997, #63e6be)' }
      ],

      referralSteps: [
        {
          icon: 'bx bx-home-heart',
          title: 'Health Post',
          description: 'Start here for basic care and health education'
        },
        {
          icon: 'bx bx-plus-medical',
          title: 'Health Center',
          description: 'Referred for outpatient care and diagnostics'
        },
        {
          icon: 'bx bx-building',
          title: 'Sub-County Hospital',
          description: 'Referred for inpatient care and surgery'
        },
        {
          icon: 'bx bx-hospital',
          title: 'County Hospital',
          description: 'Referred for specialized treatment'
        }
      ],

      comparisonFacilities: ['Health Post', 'Health Center', 'Sub-County Hospital', 'County Hospital'],

      serviceComparison: [
        { name: 'Health Education', icon: 'bx bx-book-reader', availability: [true, true, true, true] },
        { name: 'Immunization', icon: 'bx bx-injection', availability: [true, true, true, true] },
        { name: 'Family Planning', icon: 'bx bx-heart', availability: [true, true, true, true] },
        { name: 'Outpatient Care', icon: 'bx bx-clinic', availability: [false, true, true, true] },
        { name: 'Laboratory Tests', icon: 'bx bx-test-tube', availability: [false, true, true, true] },
        { name: 'Pharmacy', icon: 'bx bx-capsule', availability: [false, true, true, true] },
        { name: 'Maternal Care', icon: 'bx bx-female', availability: [true, true, true, true] },
        { name: 'Delivery Services', icon: 'bx bx-child', availability: [false, true, true, true] },
        { name: 'Minor Surgery', icon: 'bx bx-cut', availability: [false, true, true, true] },
        { name: 'Inpatient Care', icon: 'bx bx-bed', availability: [false, false, true, true] },
        { name: 'Major Surgery', icon: 'bx bx-knife', availability: [false, false, true, true] },
        { name: 'X-Ray/Imaging', icon: 'bx bx-scan', availability: [false, false, true, true] },
        { name: 'ICU Services', icon: 'bx bx-pulse', availability: [false, false, false, true] },
        { name: 'Specialist Care', icon: 'bx bx-user-voice', availability: [false, false, false, true] },
        { name: 'CT/MRI', icon: 'bx bx-radio-circle', availability: [false, false, false, true] }
      ],

      qualityStandards: [
        {
          icon: 'bx bx-certification',
          title: 'MOH Accreditation',
          description: 'All facilities must be accredited by the Ministry of Health'
        },
        {
          icon: 'bx bx-user-check',
          title: 'Licensed Staff',
          description: 'Healthcare workers must hold valid professional licenses'
        },
        {
          icon: 'bx bx-box',
          title: 'Essential Equipment',
          description: 'Facilities must maintain minimum required medical equipment'
        },
        {
          icon: 'bx bx-capsule',
          title: 'Medicine Availability',
          description: 'Essential medicines must be stocked and properly stored'
        },
        {
          icon: 'bx bx-water',
          title: 'WASH Standards',
          description: 'Clean water, sanitation, and hygiene facilities required'
        },
        {
          icon: 'bx bx-search-alt',
          title: 'Regular Inspections',
          description: 'Annual quality audits and compliance monitoring'
        }
      ],

      findMethods: [
        {
          icon: 'bx bx-phone-call',
          title: 'Call Hotline',
          description: 'Contact our helpline to get facility information for your area.',
          color: 'primary',
          details: [
            { icon: 'bx bx-phone', label: 'Phone', value: '+254 700 000 000' },
            { icon: 'bx bx-time', label: 'Hours', value: '24/7' }
          ]
        },
        {
          icon: 'bx bx-buildings',
          title: 'Visit Ward Office',
          description: 'Your local ward administration has a list of nearby PONA HEALTH-X facilities.',
          color: 'success',
          details: [
            { icon: 'bx bx-map', label: 'Location', value: 'Your Ward Office' },
            { icon: 'bx bx-time', label: 'Hours', value: '8:30 AM - 5:30 PM' }
          ]
        },
        {
          icon: 'bx bx-clinic',
          title: 'Ask at Health Center',
          description: 'Any health center can provide information about the facility network.',
          color: 'info',
          details: [
            { icon: 'bx bx-map', label: 'Location', value: 'Nearest Health Center' },
            { icon: 'bx bx-time', label: 'Hours', value: '8:00 AM - 6:00 PM' }
          ]
        }
      ],

      amenities: [
        { icon: 'bx bx-chair', title: 'Waiting Areas', description: 'Comfortable seating for patients and families' },
        { icon: 'bx bx-water', title: 'Clean Water', description: 'Safe drinking water available' },
        { icon: 'bx bx-bath', title: 'Sanitation', description: 'Clean toilet and handwashing facilities' },
        { icon: 'bx bx-shield-check', title: 'Safety', description: 'Secure environment for patients' },
        { icon: 'bx bx-handicap', title: 'Accessibility', description: 'Accommodations for disabled persons' },
        { icon: 'bx bx-info-circle', title: 'Information Desk', description: 'Staff to guide and assist patients' },
        { icon: 'bx bx-first-aid', title: 'Emergency Access', description: '24/7 emergency services where available' },
        { icon: 'bx bx-phone', title: 'Communication', description: 'Phone access for referrals' }
      ],

      faqs: [
        {
          question: 'How do I know if a facility is contracted with PONA HEALTH-X?',
          answer: 'All public health facilities (health posts, health centers, and hospitals) are automatically contracted with PONA HEALTH-X. Some private facilities may also be contracted. Look for the PONA HEALTH-X logo or ask at the facility. You can also call +254 700 000 000 to verify.'
        },
        {
          question: 'Can I visit any facility with my PONA HEALTH-X card?',
          answer: 'You should first visit your designated health center for primary care. For specialized services, you\'ll need a referral. However, in emergencies, you can visit any PONA HEALTH-X-contracted facility directly without a referral.'
        },
        {
          question: 'What if there\'s no PONA HEALTH-X facility near me?',
          answer: 'PONA HEALTH-X has extensive coverage with over 1,800 health posts reaching most communities. If you\'re in a remote area, contact your district health office for the nearest facility. The Community Health Program also brings services to rural areas.'
        },
        {
          question: 'Are private hospitals covered by PONA HEALTH-X?',
          answer: 'Some private facilities have contracts with PONA HEALTH-X, but not all. Coverage at private facilities depends on your region\'s agreements. It\'s best to check with the facility or call +254 700 000 000 before visiting a private hospital.'
        },
        {
          question: 'What happens if I need services not available at my facility?',
          answer: 'If your facility cannot provide the services you need, they will refer you to a higher-level facility. The referral letter ensures your PONA HEALTH-X coverage continues at the referral facility. Always follow the referral pathway for non-emergency care.'
        },
        {
          question: 'How are facility quality standards monitored?',
          answer: 'The Ministry of Health conducts regular inspections and quality assessments of all contracted facilities. Facilities must meet minimum standards for staffing, equipment, medicines, and infrastructure to maintain their PONA HEALTH-X contract.'
        }
      ]
    };
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.observeElements();
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    goToPage(page) {
      this.$router.push(page);
    },

    observeElements() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      document.querySelectorAll('.animate-on-scroll').forEach(el => {
        observer.observe(el);
      });
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    handleScroll() {
      this.showScrollTop = window.pageYOffset > 300;
    },

    goToMemberLogin() {
      this.$router.push('/member-login');
    },

    goToStaffLogin() {
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
.facilities-page {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow-x: hidden;
  background: #fafbfc;
}

.navbar {
  padding: 1rem 0;
  transition: all 0.4s ease;
  z-index: 1001;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
}

.nav-link {
  font-weight: 600;
  color: #495057 !important;
  transition: all 0.3s ease;
}

.nav-link:hover, .nav-link.active {
  color: #2c5aa0 !important;
}

.dropdown-menu {
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border-radius: 1rem;
  padding: 0.5rem;
}

.dropdown-item {
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  font-weight: 500;
}

.dropdown-item:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  transform: translateX(5px);
}

.page-header {
  background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 50%, #4a90e2 100%);
  padding-top: 120px;
  padding-bottom: 80px;
  position: relative;
  overflow: hidden;
}

.header-map-bg {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="30" r="2" fill="rgba(255,255,255,0.1)"/><circle cx="50" cy="20" r="1.5" fill="rgba(255,255,255,0.08)"/><circle cx="80" cy="40" r="2" fill="rgba(255,255,255,0.1)"/><circle cx="30" cy="70" r="1.5" fill="rgba(255,255,255,0.08)"/><circle cx="70" cy="80" r="2" fill="rgba(255,255,255,0.1)"/><line x1="20" y1="30" x2="50" y2="20" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/><line x1="50" y1="20" x2="80" y2="40" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/><line x1="20" y1="30" x2="30" y2="70" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/><line x1="80" y1="40" x2="70" y2="80" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/></svg>');
  opacity: 0.5;
}

.header-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: radial-gradient(circle at 30% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
}

.z-2 { z-index: 2; }
.min-vh-50 { min-height: 50vh; }

.breadcrumb-light { background: transparent; margin: 0; padding: 0; }
.breadcrumb-light .breadcrumb-item a { color: rgba(255, 255, 255, 0.7); text-decoration: none; }
.breadcrumb-light .breadcrumb-item a:hover { color: white; }
.breadcrumb-light .breadcrumb-item.active { color: white; }
.breadcrumb-light .breadcrumb-item + .breadcrumb-item::before { color: rgba(255, 255, 255, 0.5); }

.header-stats-row { margin-top: 2rem; }

.header-stat-item {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 1rem 1.5rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  min-width: 140px;
}

.header-stat-item .stat-number {
  display: block;
  font-size: 1.75rem;
  font-weight: 900;
  color: white;
  line-height: 1;
}

.header-stat-item .stat-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
}

.header-facility-cards {
  position: relative;
  height: 350px;
}

.facility-float-card {
  position: absolute;
  background: white;
  padding: 1.25rem 1.5rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  animation: cardFloat 4s ease-in-out infinite;
}

.facility-float-card.card-1 { top: 0; left: 10%; animation-delay: 0s; }
.facility-float-card.card-2 { top: 120px; right: 0; animation-delay: 1s; }
.facility-float-card.card-3 { bottom: 40px; left: 20%; animation-delay: 2s; }

@keyframes cardFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
}

.ffc-icon {
  width: 50px; height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.ffc-number { display: block; font-size: 1.25rem; font-weight: 900; color: #1e3f70; line-height: 1; }
.ffc-label { font-size: 0.8rem; color: #6c757d; }

.stats-section { margin-top: -50px; position: relative; z-index: 10; padding-bottom: 2rem; }

.stats-card {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 1rem;
  transition: all 0.3s ease;
}

.stat-item:hover { background: #f8f9fa; transform: translateY(-3px); }

.stat-icon {
  width: 60px; height: 60px;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: white;
  flex-shrink: 0;
}

.stat-value { display: block; font-size: 1.5rem; font-weight: 900; color: #1e3f70; line-height: 1; }
.stat-name { font-size: 0.9rem; color: #6c757d; }

.section-label, .section-label-light {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.label-icon {
  width: 45px; height: 45px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #2c5aa0;
}

.section-label-light .label-icon { background: rgba(255, 255, 255, 0.2); color: white; }

.label-text {
  font-weight: 700;
  font-size: 0.9rem;
  color: #2c5aa0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.section-label-light .label-text { color: rgba(255, 255, 255, 0.9); }

.text-gradient {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.bg-primary { background: linear-gradient(135deg, #2c5aa0, #4a90e2) !important; }
.bg-success { background: linear-gradient(135deg, #28a745, #5cb85c) !important; }
.bg-warning { background: linear-gradient(135deg, #ffc107, #ffda6a) !important; }
.bg-info { background: linear-gradient(135deg, #17a2b8, #5bc0de) !important; }
.bg-purple { background: linear-gradient(135deg, #6f42c1, #9d7bd1) !important; }

.facility-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.facility-type-card {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
}

.facility-type-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(44, 90, 160, 0.15);
}

.type-level {
  position: absolute;
  top: 1rem; right: 1rem;
  background: #f0f0f0;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #6c757d;
}

.type-icon {
  width: 80px; height: 80px;
  border-radius: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: white;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
}

.facility-type-card:hover .type-icon { transform: scale(1.1) rotate(5deg); }

.type-info .type-icon { background: linear-gradient(135deg, #17a2b8, #5bc0de); }
.type-success .type-icon { background: linear-gradient(135deg, #28a745, #5cb85c); }
.type-warning .type-icon { background: linear-gradient(135deg, #ffc107, #ffda6a); }
.type-primary .type-icon { background: linear-gradient(135deg, #2c5aa0, #4a90e2); }

.type-description { color: #6c757d; margin-bottom: 1.5rem; line-height: 1.6; }

.type-stats {
  display: flex;
  gap: 2rem;
  padding: 1rem 0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 1.5rem;
}

.ts-item { text-align: center; }
.ts-value { display: block; font-size: 1.25rem; font-weight: 900; color: #1e3f70; }
.ts-label { font-size: 0.8rem; color: #6c757d; }

.type-services h6 { color: #1e3f70; }
.type-services ul { list-style: none; padding: 0; margin: 0 0 1.5rem 0; }

.type-services li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0;
  font-size: 0.9rem;
  color: #495057;
}

.type-services li i { color: #28a745; }

.type-staff h6 { color: #1e3f70; }
.staff-tags { display: flex; flex-wrap: wrap; gap: 0.5rem; }

.staff-tags span {
  background: #e3f2fd;
  color: #2c5aa0;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
}

.hierarchy-container { max-width: 900px; margin: 0 auto; }
.hierarchy-level { position: relative; }

.level-connector {
  width: 4px; height: 40px;
  background: linear-gradient(180deg, #2c5aa0, #4a90e2);
  margin: 0 auto;
}

.level-card {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 1rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 5px solid;
}

.level-card:hover {
  transform: translateX(10px);
  box-shadow: 0 15px 40px rgba(44, 90, 160, 0.15);
}

.level-info { border-color: #17a2b8; }
.level-success { border-color: #28a745; }
.level-warning { border-color: #ffc107; }
.level-primary { border-color: #2c5aa0; }

.level-icon {
  width: 60px; height: 60px;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: white;
  flex-shrink: 0;
}

.level-info .level-icon { background: linear-gradient(135deg, #17a2b8, #5bc0de); }
.level-success .level-icon { background: linear-gradient(135deg, #28a745, #5cb85c); }
.level-warning .level-icon { background: linear-gradient(135deg, #ffc107, #ffda6a); }
.level-primary .level-icon { background: linear-gradient(135deg, #2c5aa0, #4a90e2); }

.level-content { flex: 1; }
.level-content p { color: #6c757d; font-size: 0.9rem; margin-bottom: 0.5rem; }
.level-facilities { display: flex; flex-wrap: wrap; gap: 0.5rem; }

.facility-tag {
  background: #f8f9fa;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.8rem;
  color: #495057;
}

.level-coverage { text-align: right; }
.coverage-label { display: block; font-size: 0.75rem; color: #6c757d; }
.coverage-value { font-weight: 700; color: #1e3f70; font-size: 0.9rem; }

.region-card {
  background: white;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
}

.region-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(44, 90, 160, 0.15);
}

.region-header {
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.region-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.8rem;
  color: white;
}

.region-body { padding: 1.5rem; }

.region-stats {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.rs-item { display: flex; align-items: center; gap: 1rem; }
.rs-item i { font-size: 1.5rem; }
.rs-value { display: block; font-weight: 900; color: #1e3f70; line-height: 1; }
.rs-label { font-size: 0.8rem; color: #6c757d; }

.region-coverage { text-align: center; }

.coverage-bar {
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.coverage-fill {
  height: 100%;
  background: linear-gradient(90deg, #28a745, #5cb85c);
  border-radius: 4px;
  transition: width 1s ease;
}

.coverage-text { font-size: 0.85rem; color: #28a745; font-weight: 600; }

.bg-gradient-primary { background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 50%, #4a90e2 100%); }

.referral-pathway {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 3rem;
}

.pathway-step { display: flex; align-items: center; gap: 1rem; }

.pathway-icon {
  width: 80px; height: 80px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  transition: all 0.3s ease;
}

.pathway-step:hover .pathway-icon {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.pathway-content { max-width: 180px; }
.pathway-content h5 { color: white; }
.pathway-arrow { font-size: 2rem; color: rgba(255, 255, 255, 0.5); margin: 0 0.5rem; }

.referral-notes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.note-card {
  display: flex;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 1.5rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.note-card i { font-size: 2rem; color: #4ade80; flex-shrink: 0; }
.note-content h6 { color: white; }

.services-comparison {
  background: white;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.comparison-header {
  display: grid;
  grid-template-columns: 2fr repeat(4, 1fr);
  background: linear-gradient(135deg, #1e3f70, #2c5aa0);
  padding: 1rem;
}

.ch-service { font-weight: 700; color: white; padding: 0.5rem 1rem; }
.ch-facility { font-weight: 700; color: white; text-align: center; padding: 0.5rem; font-size: 0.85rem; }

.comparison-row {
  display: grid;
  grid-template-columns: 2fr repeat(4, 1fr);
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
}

.comparison-row:hover { background: #f8f9fa; }
.comparison-row:last-child { border-bottom: none; }

.cr-service {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  font-weight: 500;
}

.cr-service i { font-size: 1.25rem; color: #2c5aa0; }
.cr-facility { display: flex; align-items: center; justify-content: center; padding: 1rem; }
.cr-facility i { font-size: 1.5rem; }

.quality-checklist { display: flex; flex-direction: column; gap: 1rem; }

.checklist-item {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: #f8f9fa;
  border-radius: 1rem;
  transition: all 0.3s ease;
}

.checklist-item:hover { background: #e3f2fd; transform: translateX(10px); }

.checklist-icon {
  width: 50px; height: 50px;
  background: linear-gradient(135deg, #2c5aa0, #4a90e2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.quality-image-container { position: relative; }
.quality-image-container img { box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15); }

.quality-badge {
  position: absolute;
  top: 20px; left: 20px;
  background: linear-gradient(135deg, #28a745, #5cb85c);
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 0.9rem;
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4);
}

.quality-stats-card {
  position: absolute;
  bottom: -20px; right: 20px;
  background: white;
  padding: 1.25rem 2rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.qs-item { text-align: center; }
.qs-value { display: block; font-size: 1.25rem; font-weight: 900; color: #1e3f70; line-height: 1; }
.qs-label { font-size: 0.8rem; color: #6c757d; }
.qs-divider { width: 1px; height: 40px; background: #e9ecef; }

.find-card {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  text-align: center;
}

.find-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(44, 90, 160, 0.15);
}

.find-icon {
  width: 80px; height: 80px;
  border-radius: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2.5rem;
  color: white;
  transition: all 0.3s ease;
}

.find-card:hover .find-icon { transform: scale(1.1) rotate(5deg); }

.find-details {
  text-align: left;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
}

.detail-row { display: flex; align-items: center; gap: 0.75rem; padding: 0.5rem 0; }
.detail-row i { font-size: 1.25rem; color: #2c5aa0; }
.detail-label { display: block; font-size: 0.8rem; color: #6c757d; }
.detail-value { font-weight: 700; color: #1e3f70; }

.facility-contact-card {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  padding: 2.5rem;
  border-radius: 1.5rem;
  margin-top: 3rem;
  border: 2px solid #2c5aa0;
}

.amenity-card {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  text-align: center;
}

.amenity-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(44, 90, 160, 0.15);
}

.amenity-icon {
  width: 70px; height: 70px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
  color: #2c5aa0;
  transition: all 0.3s ease;
}

.amenity-card:hover .amenity-icon {
  background: linear-gradient(135deg, #2c5aa0, #4a90e2);
  color: white;
  transform: scale(1.1);
}

.accordion-item {
  margin-bottom: 1rem;
  border: none;
  border-radius: 1rem !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.accordion-button {
  font-weight: 600;
  color: #1e3f70;
  background-color: white;
  padding: 1.25rem 1.5rem;
}

.accordion-button:not(.collapsed) {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1e3f70;
  box-shadow: none;
}

.accordion-button:focus { box-shadow: none; }
.accordion-body { padding: 1.5rem; line-height: 1.8; color: #495057; }

.btn {
  padding: 0.875rem 2rem;
  border-radius: 0.75rem;
  font-weight: 700;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  border-color: transparent;
  color: white;
}

.btn-primary:hover { background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 100%); }
.btn-light { background: white; color: #2c5aa0; }
.btn-outline-light { color: white; border-color: white; }
.btn-outline-light:hover { background: white; color: #2c5aa0; }

footer { background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 100%); }
.footer-links { list-style: none; padding: 0; }
.footer-links li { margin-bottom: 0.5rem; }
.footer-links a { color: rgba(255, 255, 255, 0.6); text-decoration: none; transition: all 0.3s ease; }
.footer-links a:hover { color: white; padding-left: 8px; }
.footer-contact { list-style: none; padding: 0; }
.footer-contact li { color: rgba(255, 255, 255, 0.6); margin-bottom: 0.75rem; display: flex; align-items: center; }

.social-links { display: flex; gap: 1rem; }

.social-links .social-link {
  width: 40px; height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  transition: all 0.3s ease;
}

.social-links .social-link:hover { background: white; color: #2c5aa0; transform: translateY(-5px); }

.scroll-top-btn {
  position: fixed;
  bottom: 35px; right: 35px;
  width: 55px; height: 55px;
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  color: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(44, 90, 160, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;
  font-size: 1.8rem;
}

.scroll-top-btn:hover {
  transform: translateY(-8px) scale(1.1);
  box-shadow: 0 15px 40px rgba(44, 90, 160, 0.6);
}

.animate-on-scroll {
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.animate-on-scroll.animate-in { opacity: 1; transform: translateY(0); }

@media (max-width: 992px) {
  .header-facility-cards { display: none; }
  .comparison-header, .comparison-row { grid-template-columns: 1.5fr repeat(4, 1fr); }
  .ch-facility { font-size: 0.7rem; }
  .pathway-step { flex-direction: column; text-align: center; }
  .pathway-content { max-width: 100%; }
  .pathway-arrow { transform: rotate(90deg); margin: 0.5rem 0; }
}

@media (max-width: 768px) {
  .page-header { padding-top: 100px; }
  .page-header h1 { font-size: 2.5rem; }
  .header-stats-row { flex-direction: column; }
  .header-stat-item { width: 100%; }
  .comparison-header, .comparison-row { grid-template-columns: 1fr; }
  .ch-service, .ch-facility, .cr-service, .cr-facility { padding: 0.75rem; }
  .cr-service { background: #f8f9fa; font-weight: 700; }
  .level-card { flex-direction: column; text-align: center; }
  .level-coverage { text-align: center; margin-top: 1rem; padding-top: 1rem; border-top: 1px solid #f0f0f0; width: 100%; }
  .facility-contact-card { text-align: center; }
  .quality-stats-card { display: none; }
}
</style>